import type { ApexOptions } from "apexcharts";
import { htmlEscape } from "./escape";

/**
 * Zentraler Wrapper für ApexCharts-Optionen (Datenschutz-Gate 05.10., Ausnahme zu r121
 * auf feat/preline). Regeln:
 *  - Alle Texte aus Daten (Kategorien, Reihenname) werden hier und nur hier HTML-escaped.
 *  - Kein `tooltip.custom`, kein `markers.customHTML` (nur Konstanten wären erlaubt; dieser
 *    Wrapper braucht keine): Apex-Standard-Tooltip mit bereits escapten Texten.
 *  - Nur Aggregate (Zählwerte je Kategorie), keine personenbezogenen Zeitreihen. Bei
 *    Beschäftigtenbezug dürfen nur Gruppen ab 5 übergeben werden (Aufrufer-Pflicht).
 *
 * Optionen aus dem Preline-Block data-display/data-display-sub-analytics-kpis/data-display-cat-bar-charts/progress-row-bar-chart-card
 * (Init-Skript des Blocks, buildChart-Teil „shared“ + Hell-Variante; Farben über die Preline-Tokens
 * --chart-colors-*). Abweichungen: Tooltip ohne buildTooltip (siehe oben), ganze Zahlen auf der y-Achse.
 */
export interface BalkenDaten {
  reihenName: string;
  kategorien: string[];
  werte: number[];
}

export interface DiagrammFarben {
  primaer: string;
  gitter: string;
  xAchse: string;
  yAchse: string;
  schrift: string;
}

export function baueBalkenOptionen(daten: BalkenDaten, farben: DiagrammFarben): ApexOptions {
  const kategorien = daten.kategorien.map(htmlEscape);
  const achsenSchrift = { fontSize: "13px", fontFamily: farben.schrift, fontWeight: 400 };
  return {
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: farben.schrift,
    },
    series: [{ name: htmlEscape(daten.reihenName), data: [...daten.werte] }],
    colors: [farben.primaer],
    fill: { pattern: { width: 6, height: 6, strokeWidth: 2 } },
    grid: { strokeDashArray: 3, borderColor: farben.gitter },
    plotOptions: { bar: { horizontal: false, columnWidth: "95%", borderRadius: 2 } },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 3, colors: ["transparent"] },
    xaxis: {
      categories: kategorien,
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
      labels: { style: { colors: farben.xAchse, ...achsenSchrift }, offsetX: -2 },
    },
    yaxis: {
      decimalsInFloat: 0,
      labels: {
        align: "left",
        minWidth: 0,
        maxWidth: 140,
        style: { colors: farben.yAchse, ...achsenSchrift },
        formatter: (wert: number) => (wert >= 1000 ? `${wert / 1000}k` : String(Math.round(wert))),
      },
    },
    tooltip: { enabled: true },
    states: { hover: { filter: { type: "darken" } } },
    responsive: [
      {
        breakpoint: 568,
        options: {
          chart: { height: 300 },
          stroke: { width: 8 },
          yaxis: {
            labels: {
              align: "left",
              minWidth: 0,
              maxWidth: 140,
              style: { colors: farben.yAchse, fontSize: "11px", fontFamily: farben.schrift, fontWeight: 400 },
            },
          },
        },
      },
    ],
  };
}
